import forecastsService from "@/components/forecasts/forecastsService";
import ForecastValidator from "@/components/forecasts/validation/forecastValidator";
import { FORECAST_STATUS } from "@/components/forecasts/constants";

export const forecast = {
    namespaced: true,
    state: {
        forecasts: [],
        isShowActiveOnly: false,
        brandZones: [],
        adminBrandZones: [],
        adminBrandZone: [],
        deleted: false,
        activeAmount: 0,
        isPopulated: false,
        isBrandZonesPopulated: false,
        isBrandZonePopulated: false,
        itemsShallow: []
    },
    getters: {
        all: state => state.forecasts,
        active: state => state.forecasts.filter(forecast => forecast.status === FORECAST_STATUS.ACTIVE && !forecast.deleted),
        updatable: state => state.forecasts.filter(forecast => ForecastValidator.canUpdate(forecast)),
        itemsModifiable: state => state.forecasts.filter(forecast => ForecastValidator.canModifyItems(forecast)),
        getForecast: state => id => state.forecasts.find(forecast => forecast.id === parseInt(id)),
        getItemsShallow: state => forecastId => state.itemsShallow[forecastId],
        isShowActiveOnly: state => state.isShowActiveOnly,
        getBrandZones: state => state.brandZones,
        getAdminBrandZones: state => state.adminBrandZones,
        getAdminBrandZone: state => state.adminBrandZone,
        getActiveAmount: state => state.activeAmount
    },
    mutations: {
        SET_FORECASTS(state, data) {
            state.forecasts = data;
        },
        UPDATE_FORECAST(state, data) {
            let forecasts = state.forecasts;
            forecasts = forecasts.map(forecast => {
                if (forecast.id === data.id) {
                    return data;
                } else {
                    return forecast;
                }
            });
            state.forecasts = forecasts;
        },
        SET_IS_SHOW_ACTIVE_ONLY(state, data) {
            state.isShowActiveOnly = data;
        },
        SET_BRAND_ZONES(state, data) {
            state.brandZones = data;
        },
        SET_ADMIN_BRAND_ZONES(state, data) {
            state.adminBrandZones = data;
        },
        SET_ADMIN_BRAND_ZONE(state, data) {
            state.adminBrandZone = data;
        },
        SET_ACTIVE_AMOUNT(state, data) {
            state.activeAmount = data;
        },
        SET_IS_POPULATED(state, data) {
            state.isPopulated = data;
        },
        SET_IS_BRAND_ZONES_POPULATED(state, data) {
            state.isBrandZonesPopulated = data;
        },
        SET_IS_BRAND_ZONE_POPULATED(state, data) {
            state.isBrandZonePopulated = data;
        },
        SET_ITEMS_SHALLOW(state, data) {
            state.itemsShallow = data;
        }
    },
    actions: {
        async populate({ commit, state }) {
            if (!state.isPopulated) {
                const forecasts = await forecastsService.getForecasts();
                commit("SET_FORECASTS", forecasts);
                commit("SET_IS_POPULATED", true);
                return forecasts;
            } else {
                return state.forecasts;
            }
        },
        async populateZones({ commit, state }) {
            const brandZones = await forecastsService.getBrandZones();
            commit("SET_BRAND_ZONES", brandZones);
            return brandZones;
        },
        async populateAdminBrandZones({ commit, state }, data = {}) {
            if (!state.isBrandZonesPopulated || data.reload) {
                let adminBrandZones = await forecastsService.getAdminBrandZones(data.app, data.withDeleted);
                adminBrandZones = adminBrandZones.sort((bz1, bz2) => {
                    return bz1.zone.name.toLowerCase() > bz2.zone.name.toLowerCase() ? 1 : -1;
                });
                if (!data.notPersist) {
                    commit("SET_ADMIN_BRAND_ZONES", adminBrandZones);
                    commit("SET_IS_BRAND_ZONES_POPULATED", true);
                }
                return adminBrandZones;
            } else {
                return state.adminBrandZones;
            }
        },
        async populateAdminBrandZone({ commit, state }, { zoneId, reload }) {
            if (!state.isBrandZonePopulated || reload) {
                const adminBrandZone = await forecastsService.getAdminBrandZone(zoneId);
                commit("SET_ADMIN_BRAND_ZONE", adminBrandZone);
                commit("SET_IS_BRAND_ZONE_POPULATED", true);
                return adminBrandZone;
            } else {
                return state.adminBrandZone;
            }
        },
        async populateItemsShallow({ commit, state }) {
            if (state.itemsShallow.length > 0) {
                return;
            }
            let itemsShallow = [];
            for (let forecast of state.forecasts) {
                // @todo: fetch async
                if (ForecastValidator.canUpdate(forecast)) {
                    let items = await forecastsService.getItems(forecast.id);
                    itemsShallow[forecast.id] = items;
                }
            }
            commit("SET_ITEMS_SHALLOW", itemsShallow);
        },
        clear({ commit }) {
            commit("SET_FORECASTS", []);
        },
        async createForecast({ commit, state }, forecast) {
            let newForecast = await forecastsService.createForecast(forecast);
            let forecasts = state.forecasts;
            forecasts.push(newForecast);
            commit("SET_FORECASTS", forecasts);
            return newForecast;
        },
        async updateForecast({ commit, state }, forecast) {
            console.assert(forecast, "No forecast given");
            try {
                const updatedForecast = await forecastsService.updateForecast(forecast);
                commit("UPDATE_FORECAST", updatedForecast);
            } catch (e) {
                // rethrow exception to handle it in the view
                throw e;
            }
        },
        async publishForecast({ commit, state }, { forecast, onlyAffiliate }) {
            console.assert(forecast, "No forecast given");
            try {
                const updatedForecast = await forecastsService.publishForecast(forecast, onlyAffiliate);
                commit("UPDATE_FORECAST", updatedForecast);
            } catch (e) {
                console.error(e);
                window.alert(e);
            }
        },
        async duplicateForecast({ commit, state }, { forecast, name, reference }) {
            console.assert(forecast, "No forecast given");
            console.assert(name, "No name given");
            console.assert(reference, "No reference given");

            const duplicatedForecast = forecastsService.duplicateForecast(forecast, name, reference);
            let forecasts = state.forecasts;
            forecasts.push(duplicatedForecast);
            commit("SET_FORECASTS", forecasts);

            return duplicatedForecast;
        },
        async deleteForecast({ commit, state }, forecast) {
            console.assert(forecast, "No forecast given");
            try {
                await forecastsService.deleteForecast(forecast);
                let updatedForecast = { ...forecast, deleted: true };
                commit("UPDATE_FORECAST", updatedForecast);
            } catch (e) {
                // rethrow exception to handle it in the view
                throw e;
            }
        },
        async updateItems({ commit, dispatch }, { forecast, itemsToAdd, itemsToRemove }) {
            console.assert(forecast, "No forecast given");

            try {
                itemsToAdd = itemsToAdd || [];
                itemsToRemove = itemsToRemove || [];
                let resp = await forecastsService.updateItems(forecast, itemsToAdd, itemsToRemove);
                if (resp.hasOwnProperty("addedItems")) {
                    let updatedForecast = forecast;
                    updatedForecast.numberOfItems = updatedForecast.numberOfItems + parseInt(resp.addedItems);
                    commit("UPDATE_FORECAST", updatedForecast);
                }
                if (resp.hasOwnProperty("removedItems")) {
                    let updatedForecast = forecast;
                    updatedForecast.numberOfItems = updatedForecast.numberOfItems - parseInt(resp.removedItems);
                    commit("UPDATE_FORECAST", updatedForecast);
                }
                commit("SET_ITEMS_SHALLOW", []);
            } catch (e) {
                console.error(e);
                window.alert(e);
            }
        },
        async updateItemsFromImport({ commit, dispatch }, { forecast, items }) {
            console.assert(forecast, "No forecast given");

            try {
                let resp = await forecastsService.updateItemsFromImport(forecast, items);
                if (resp.hasOwnProperty("addedItems")) {
                    let updatedForecast = forecast;
                    updatedForecast.numberOfItems = updatedForecast.numberOfItems + parseInt(resp.addedItems);
                    commit("UPDATE_FORECAST", updatedForecast);
                }
                if (resp.hasOwnProperty("removedItems")) {
                    let updatedForecast = forecast;
                    updatedForecast.numberOfItems = updatedForecast.numberOfItems - parseInt(resp.removedItems);
                    commit("UPDATE_FORECAST", updatedForecast);
                }
                commit("SET_ITEMS_SHALLOW", []);
            } catch (e) {
                console.error(e);
                window.alert(e);
            }
        },
        async setIsShowActiveOnly({ commit }, data) {
            commit("SET_IS_SHOW_ACTIVE_ONLY", data);
        },
        async populateActiveAmount({ commit }, { isForecastAdmin }) {
            try {
                let activeAmount = isForecastAdmin
                    ? await forecastsService.getActiveAmountForecasts()
                    : await forecastsService.getActiveAmountForecastWindows();
                commit("SET_ACTIVE_AMOUNT", activeAmount.activeAmount);
            } catch (e) {
                console.error(e);
            }
        },
        async reChallengeForecast({ commit, state }, { forecast, forecastWindows, message }) {
            console.assert(forecast, "No forecast given");
            console.assert(forecastWindows, "No forecastWindows given");
            try {
                const updatedForecast = await forecastsService.reChallengeForecastWindows({ forecast, forecastWindows, message });
                commit("UPDATE_FORECAST", updatedForecast);
            } catch (e) {
                console.error(e);
                window.alert(e);
            }
        },
        async refreshForecast({ commit, state }, forecast) {
            console.assert(forecast, "No forecast given");
            try {
                const refreshedForecast = await forecastsService.getForecast(forecast.id);
                commit("UPDATE_FORECAST", refreshedForecast);
            } catch (e) {
                console.error(e);
                window.alert(e);
            }
        }
    }
};



// WEBPACK FOOTER //
// ./src/store/forecast/forecast.js